/**
 * ScenePreviewCanvasWrapper Component
 *
 * Wrapper that renders overlays using the Remotion-based editor.
 * Fabric.js rendering has been removed in favor of Remotion-only rendering
 * for consistency between preview and export.
 */

import React, { useCallback } from "react";
import { AnyOverlay, ImageFilter } from "../models/Post";
import { getNextZIndex } from "../utils/textOverlayUtils";
import { RemotionScenePreview } from "./remotion-editor/RemotionScenePreview";

// Editor mode type - kept for backward compatibility but only 'remotion' is used
export type EditorMode = "remotion";

// Module-level clipboard shared across all canvas instances (persists across slides)
let overlayClipboard: AnyOverlay | null = null;

interface ScenePreviewCanvasWrapperProps {
  backgroundColor?: string;
  backgroundImagePath?: string;
  backgroundImageUrl?: string;
  backgroundFilter?: ImageFilter;
  overlays: AnyOverlay[];
  onOverlayChange?: (updatedOverlay: AnyOverlay) => void;
  onSelectOverlay?: (overlayId: string | null) => void;
  selectedOverlayId?: string | null;
  overlayPreviewUrls?: Record<string, string>;
  referenceMediaWidth?: number;
  /** Called once when a drag/resize/rotate interaction begins on the canvas */
  onInteractionStart?: () => void;
  /** Called when backgroundImageUrl is resolved from backgroundImagePath fallback */
  onBackgroundImageResolved?: (url: string) => void;
  onDeleteOverlay?: (id: string) => void;
  onDuplicateOverlay?: (id: string) => void;
  /** Called to add a new overlay (e.g. paste from clipboard) */
  onAddOverlay?: (overlay: AnyOverlay) => void;
  /** @deprecated Toggle UI has been removed. Always uses Remotion. */
  showToggle?: boolean;
  /** @deprecated Editor mode is always 'remotion'. */
  onEditorModeChange?: (mode: EditorMode) => void;
}

export const ScenePreviewCanvasWrapper: React.FC<ScenePreviewCanvasWrapperProps> = ({
  backgroundColor,
  backgroundImagePath,
  backgroundImageUrl,
  backgroundFilter,
  overlays,
  onOverlayChange,
  onSelectOverlay,
  selectedOverlayId,
  overlayPreviewUrls,
  referenceMediaWidth,
  onInteractionStart,
  onBackgroundImageResolved,
  onDeleteOverlay,
  onDuplicateOverlay,
  onAddOverlay,
  // These props are now deprecated but kept for API compatibility
  showToggle: _showToggle,
  onEditorModeChange,
}) => {
  // Notify parent of the fixed editor mode on mount
  React.useEffect(() => {
    onEditorModeChange?.("remotion");
  }, [onEditorModeChange]);

  // Copy overlay to module-level clipboard (Ctrl/Cmd+C)
  const handleCopyOverlay = useCallback((overlay: AnyOverlay) => {
    overlayClipboard = JSON.parse(JSON.stringify(overlay));
  }, []);

  // Paste overlay from clipboard (Ctrl/Cmd+V) — works across slides
  const handlePasteOverlay = useCallback(() => {
    if (!overlayClipboard || !onAddOverlay) return;

    const pasted: AnyOverlay = JSON.parse(JSON.stringify(overlayClipboard));
    pasted.id = crypto.randomUUID();
    pasted.zIndex = getNextZIndex(overlays);

    // Offset if pasting on same slide (overlay at same position exists)
    const duplicate = overlays.some(
      (o) =>
        Math.abs(o.position.x - pasted.position.x) < 1 &&
        Math.abs(o.position.y - pasted.position.y) < 1
    );
    if (duplicate) {
      pasted.position = {
        x: pasted.position.x + 5,
        y: pasted.position.y + 5,
      };
    }

    onAddOverlay(pasted);
    onSelectOverlay?.(pasted.id);
  }, [overlays, onAddOverlay, onSelectOverlay]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <RemotionScenePreview
        backgroundColor={backgroundColor}
        backgroundImagePath={backgroundImagePath}
        backgroundImageUrl={backgroundImageUrl}
        backgroundFilter={backgroundFilter}
        overlays={overlays}
        onOverlayChange={onOverlayChange}
        onSelectOverlay={onSelectOverlay}
        selectedOverlayId={selectedOverlayId}
        overlayPreviewUrls={overlayPreviewUrls}
        referenceMediaWidth={referenceMediaWidth}
        onInteractionStart={onInteractionStart}
        onBackgroundImageResolved={onBackgroundImageResolved}
        onDeleteOverlay={onDeleteOverlay}
        onDuplicateOverlay={onDuplicateOverlay}
        onCopyOverlay={handleCopyOverlay}
        onPasteOverlay={handlePasteOverlay}
      />
    </div>
  );
};

export default ScenePreviewCanvasWrapper;
